import { authHeaders, handleUnauthorized } from "./auth";
import {
  corpusStatsSchema,
  type CorpusStats,
  docgenResponseSchema,
  type AmendRequestPayload,
  type DocumentGenerationRequest,
  type DocgenResponse,
  refineResponseSchema,
  type RefineRequest,
  type RefineResponse,
  type RegenerateRequest,
  regenerateResponseSchema,
  type RegenerateResponse,
  type ExportRequest,
  type FeasibilityRequest,
  feasibilityResponseSchema,
  type FeasibilityResponse,
  gradivostChatResponseSchema,
  type GradivostChatResponse,
  legalBackboneResponseSchema,
  type LegalBackboneRequest,
  type LegalBackboneResponse,
  verifyTextResponseSchema,
  type VerifyTextRequest,
  type VerifyTextResponse,
  situacijaReportSchema,
  type SituacijaReport,
  opciDioStatusSchema,
  type OpciDioStatus,
  recognizedDocumentSchema,
  type RecognizedDocument,
  drawingVisionResultSchema,
  type DrawingVisionResult,
} from "./schemas";

/**
 * Thin fetch wrapper used by every TanStack Query hook. Goes through
 * the Vite dev proxy in development and hits the same FastAPI app in
 * production (the SPA is mounted at ``/app`` on the same origin).
 *
 * ``apiFetch`` attaches the pilot access token to every call and flips
 * the global auth gate on 401 — keep ALL backend calls on it.
 */
export async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: { ...authHeaders(), ...(init.headers ?? {}) },
  });
  handleUnauthorized(res);
  return res;
}

async function postJSON<T>(url: string, body: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: string | DenialDetail = "";
    try {
      const data = (await res.json()) as { detail?: string | DenialDetail };
      detail = data?.detail ?? "";
    } catch {
      // body wasn't JSON
    }
    if (isDenialDetail(detail)) {
      throw new DenialError(res.status, detail);
    }
    throw new ApiError(
      res.status,
      typeof detail === "string" && detail ? detail : res.statusText,
    );
  }
  return (await res.json()) as T;
}

async function postFormData(url: string, fields: Record<string, string>, files: File[]): Promise<unknown> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) fd.append(k, v);
  for (const f of files) fd.append("files", f, f.name);
  const res = await apiFetch(url, { method: "POST", body: fd });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: string };
      detail = data?.detail ?? "";
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  return await res.json();
}

/**
 * Single-file FormData POST with a configurable field name. The NL
 * endpoints (``/api/nl/transcribe``, ``/api/nl/extract-from-document``)
 * use distinct field names (``audio``, ``file``) so we can't reuse the
 * DXF helper that hard-codes ``files``. Backend errors come back as
 * either ``{detail}`` (FastAPI default) or ``{message}`` (our custom
 * JSONResponse error shape) — handle both.
 */
async function postSingleFile(
  url: string,
  fieldName: string,
  file: File,
  extraFields?: Record<string, string>,
): Promise<unknown> {
  const fd = new FormData();
  fd.append(fieldName, file, file.name);
  for (const [k, v] of Object.entries(extraFields ?? {})) fd.append(k, v);
  const res = await apiFetch(url, { method: "POST", body: fd });
  if (!res.ok) {
    let message = "";
    try {
      const data = (await res.json()) as { detail?: string; message?: string };
      message = data?.message || data?.detail || "";
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, message || res.statusText);
  }
  return await res.json();
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * L8 denial mode. Backend returns ``HTTP 422`` with
 * ``detail = { error_code: "missing_mandatory_law_text", missing: [...] }``
 * when the resolver flags an ``export_blocking`` law as missing text.
 * The DataPage renders a hard-error banner that lists each missing
 * instrument.
 */
export interface MissingLaw {
  rule_id: number;
  instrument_id: string | null;
  short_title: string;
  official_title: string;
  reason: "no_text" | "unresolved_instrument";
}

export interface DenialDetail {
  error_code: "missing_mandatory_law_text";
  message: string;
  missing: MissingLaw[];
}

export class DenialError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: DenialDetail,
  ) {
    super(detail.message);
    this.name = "DenialError";
  }
}

function isDenialDetail(detail: unknown): detail is DenialDetail {
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { error_code?: unknown }).error_code === "missing_mandatory_law_text"
  );
}

// ---- Endpoint wrappers ----------------------------------------------------

export async function generateDocuments(
  payload: DocumentGenerationRequest,
): Promise<DocgenResponse> {
  const data = await postJSON<unknown>("/api/generate-documents", payload);
  return docgenResponseSchema.parse(data);
}

/**
 * P2 — incrementally update an already-generated document. Sends the new inputs
 * plus the previous document and its input snapshot; the backend regenerates
 * only the sections an input change touched (and never the locked/edited ones),
 * returning the merged document with an ``amendment_plan``.
 */
export async function amendDocuments(payload: AmendRequestPayload): Promise<DocgenResponse> {
  const data = await postJSON<unknown>("/api/amend-documents", payload);
  return docgenResponseSchema.parse(data);
}

export async function refineSection(payload: RefineRequest): Promise<RefineResponse> {
  const data = await postJSON<unknown>("/api/refine-section", payload);
  return refineResponseSchema.parse(data);
}

export async function regenerateSection(
  payload: RegenerateRequest,
): Promise<RegenerateResponse> {
  const data = await postJSON<unknown>("/api/regenerate-section", payload);
  return regenerateResponseSchema.parse(data);
}

export async function exportDocuments(payload: ExportRequest): Promise<Blob> {
  const res = await apiFetch("/api/export/documents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Try to parse the structured denial-mode payload first; fall back
    // to a plain ApiError so legacy 4xx/5xx responses still surface.
    let parsed: unknown = null;
    try {
      parsed = await res.clone().json();
    } catch {
      // not JSON
    }
    const detail =
      parsed && typeof parsed === "object" && parsed !== null
        ? (parsed as { detail?: unknown }).detail
        : null;
    if (isDenialDetail(detail)) {
      throw new DenialError(res.status, detail);
    }
    throw new ApiError(res.status, await res.text());
  }
  return await res.blob();
}

// ---- Mapa 1 assembler -----------------------------------------------------

export interface MapaAssemblePayload {
  izjave: Array<Record<string, unknown>>;
  naslovnice: Array<Record<string, unknown>>;
  tekstualni_dio: Array<Record<string, unknown>>;
  tables: Array<Record<string, unknown>>;
  /** R8 sections (Popis mapa / Sadržaj Mape 1) — Popis binds as front matter. */
  mape?: Array<Record<string, unknown>>;
  recognized_docs: Array<Record<string, unknown>>;
  fact_sheet?: Record<string, unknown> | null;
  variables?: Record<string, unknown> | null;
  office?: Record<string, unknown> | null;
  /** S2 — špranca id ili puni model; null = Permitko standard. */
  spranca?: string | Record<string, unknown> | null;
}

export interface MapaSadrzajSubEntry {
  title: string;
  oznaka: string | null;
  start_page: number;
}

export interface MapaSadrzajEntry {
  title: string;
  group: string | null;
  oznaka: string | null;
  start_page: number;
  page_count: number;
  /** Sections inside a continuously-flowing part (tehnički opis 2.x/3.x). */
  sub_entries?: MapaSadrzajSubEntry[];
}

/** Preview the assembled Mapa 1 contents (with page numbers) — no PDF. */
export async function previewMapa1(
  payload: MapaAssemblePayload,
): Promise<{ sadrzaj: MapaSadrzajEntry[]; page_count: number }> {
  const res = await apiFetch("/api/mapa/assemble", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, preview: true }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return (await res.json()) as {
    sadrzaj: MapaSadrzajEntry[];
    page_count: number;
  };
}

/** Assemble + download the whole Mapa 1 as one bound PDF. */
export async function assembleMapa1(payload: MapaAssemblePayload): Promise<Blob> {
  const res = await apiFetch("/api/mapa/assemble", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return await res.blob();
}

// ---- D1: špranca upload ---------------------------------------------------

// Type aliases (not interfaces) on purpose: aliases carry an implicit
// index signature, so a SprancaDict is assignable to the
// ``Record<string, unknown>`` the zod request schemas expect.
export type SprancaSectionNode = {
  section_id: string;
  title: string;
  klasa: string;
  oznaka: string | null;
  depth_words: number;
  seed_text: string;
  children: SprancaSectionNode[];
};

/** Mirror of the backend ``Spranca.to_dict()`` shape. */
export type SprancaDict = {
  spranca_id: string;
  name: string;
  sections: SprancaSectionNode[];
  header: { layout: string; rows: { label: string; var: string }[] };
  footer: { pattern: string; page_label: string; show_office_identity: boolean };
  style: {
    font_family: string;
    body_pt: number;
    h1_pt: number;
    h2_pt: number;
    line_height: number;
  };
};

/** Same ``{value, confidence, source}`` shape the extraction UI speaks,
 * keyed by dotted paths (``sections.0.children.2``, ``header.rows.1.var``,
 * ``footer.pattern``, ``name``). */
export interface SprancaFieldConfidence {
  value: string;
  confidence: number;
  source: string;
}

export interface SprancaExtractResponse {
  status: string;
  spranca: SprancaDict;
  confidence: Record<string, SprancaFieldConfidence>;
  warnings: string[];
  stats: Record<string, number>;
}

/** Upload the office's own špranca (.docx/.docm/.pdf) for extraction.
 * The backend processes it in memory — nothing is persisted until the
 * user explicitly saves the reviewed result. */
export async function extractSpranca(file: File): Promise<SprancaExtractResponse> {
  const data = await postSingleFile("/api/spranca/extract", "file", file);
  return data as SprancaExtractResponse;
}

/** Persist the reviewed & confirmed špranca server-side. */
export async function saveCustomSpranca(spranca: SprancaDict): Promise<SprancaDict> {
  const res = await apiFetch("/api/spranca/custom", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ spranca }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  const body = (await res.json()) as { spranca: SprancaDict };
  return body.spranca;
}

export async function deleteCustomSpranca(): Promise<void> {
  const res = await apiFetch("/api/spranca/custom", { method: "DELETE" });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export async function callFeasibility(
  payload: FeasibilityRequest,
): Promise<FeasibilityResponse> {
  const data = await postJSON<unknown>("/api/feasibility", payload);
  return feasibilityResponseSchema.parse(data);
}

/** G2 — chat ulaz u modul gradivosti: pitanje → parse + auto-zona (G0) +
 *  potpitanje. Verdikt zatim vrti postojeći callFeasibility s hidratiziranom
 *  formom (dvostupanjski tok, t.18). Auto-zona zna trajati do ~30 s na prvi
 *  (nekeširani) upit — UI mora pokazati stanje čekanja. */
export async function gradivostChat(
  text: string,
  previous: GradivostChatResponse["parsed"] | null = null,
): Promise<GradivostChatResponse> {
  const data = await postJSON<unknown>("/api/gradivost/chat", { text, previous });
  return gradivostChatResponseSchema.parse(data);
}

/** G2 — fire-and-forget dopis verdikta u chat log (Zakon #6: eval korpus
 *  raste iz produkcije). Neuspjeh se guta — log je audit, ne uvjet. */
export function gradivostChatOutcome(logId: number, outcomeClass: string): void {
  void postJSON(`/api/gradivost/chat/${logId}/outcome`, {
    outcome_class: outcomeClass,
  }).catch(() => undefined);
}

/** Download the feasibility verdict as a shareable PDF report. Pass the raw
 *  FeasibilityResponse (it carries outcome/memo/parameters the report needs). */
export async function exportFeasibility(feasibilityData: unknown): Promise<Blob> {
  const res = await apiFetch("/api/export/feasibility", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ feasibility_data: feasibilityData }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return await res.blob();
}

/**
 * Hybrid legal backbone (Faza 2): deterministic spine + grounded agent +
 * engine recall safety-net. Heavier than the rest (reads the corpus + runs
 * the grounded reasoner), so the UI calls it on demand, not automatically.
 * The result is always ``needs_review`` — the architect pins it before it
 * reaches the izjava. Degrades to engine-only when the reasoner is down.
 */
export async function fetchLegalBackbone(
  payload: LegalBackboneRequest,
): Promise<LegalBackboneResponse> {
  const data = await postJSON<unknown>("/api/opci-dio/legal-backbone", payload);
  return legalBackboneResponseSchema.parse(data);
}

/**
 * Faza 3 — grounded text-vs-law check. Sends the generated document text and
 * (optionally) the laws in play; the backend verifies each legal claim against
 * the real article text. Advisory: verdicts are a review aid, never an edit.
 */
export async function verifyTextAgainstLaw(
  payload: VerifyTextRequest,
): Promise<VerifyTextResponse> {
  const data = await postJSON<unknown>("/api/legal/verify-text", payload);
  return verifyTextResponseSchema.parse(data);
}

/**
 * Parse an uploaded vector-PDF situacija into its declared urbanistic iskaz
 * (čestica/GBP/footprint/KIG/KISN/hortikultura/parking) + internal-consistency
 * checks. The returned report is also the exact ``situacija`` input the
 * opci-dio status / docgen endpoints accept, so the caller can feed it back in.
 */
export async function extractSituacija(file: File): Promise<SituacijaReport> {
  const data = await postSingleFile("/api/situacija/extract", "file", file);
  return situacijaReportSchema.parse(data);
}

/**
 * Classify one uploaded opći-dio document (scan or digital PDF) into its slot
 * and extract its fields (vision/text LLM server-side). Stateless: the client
 * accumulates results and feeds them back into ``fetchOpciDioStatus`` and the
 * generate request as ``recognized_docs``.
 *
 * ``expectedType`` (F1 Donesi konzola) — the console row the file was dropped
 * on; the backend treats it as one more deterministic classification signal
 * and reports a conflict as ``_expected_mismatch``. Omitted → the plain
 * basket upload, byte-for-byte today's behaviour.
 */
export async function recognizeDocument(
  file: File,
  expectedType?: string,
): Promise<RecognizedDocument> {
  const data = await postSingleFile(
    "/api/recognize-document",
    "file",
    file,
    expectedType ? { expected_type: expectedType } : undefined,
  );
  return recognizedDocumentSchema.parse(data);
}

/**
 * B1 — run the vision layer over an already-uploaded drawing (by the
 * ``doc_id`` recognizeDocument returned). Returns a qualitative description +
 * amber proposed facts. Never throws on an LLM problem: the backend degrades
 * to an empty result with ``error`` set, so callers can silently skip.
 */
export async function describeDrawing(
  docId: string,
  docType: string,
): Promise<DrawingVisionResult> {
  const data = await postJSON<unknown>("/api/describe-drawing", {
    doc_id: docId,
    doc_type: docType,
  });
  return drawingVisionResultSchema.parse(data);
}

/**
 * Fetch a persisted original document (the recognizer keeps every upload).
 * Returns a Blob — auth is header-based, so a bare ``<iframe src>`` can't
 * carry the token; the viewer wraps this in ``URL.createObjectURL``.
 */
export async function fetchFileBlob(docId: string): Promise<Blob> {
  const res = await apiFetch(`/api/files/${encodeURIComponent(docId)}`);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return await res.blob();
}

/**
 * Render one opći-dio izjava (slot 1.3–1.9) to an office-fidelity PDF Blob
 * via the office renderer — the true WYSIWYG the docgen UI embeds instead of
 * flattened text. ``slot`` accepts "1.3", "1_3", or the section_id
 * "izjava_1_3". ``variables`` are the merged uploads/manual overrides the
 * docgen used (optional — defaults to the fact-sheet-derived bridge).
 */
export async function renderIzjavaPdf(payload: {
  slot: string;
  fact_sheet: unknown;
  variables?: Record<string, unknown>;
  office?: Record<string, unknown>;
}): Promise<Blob> {
  const res = await apiFetch("/api/opci-dio/izjava-pdf", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return await res.blob();
}

/**
 * Compute the opći-dio status / canonical project model. Passing a parsed
 * ``situacija`` routes its declared figures into the matica variables
 * (footprint/KIG/KISN/GBP) with ``plane=matica`` provenance; passing the
 * accumulated ``recognized_docs`` fills the upload slots (📎), the variable
 * merge, and the posebni-uvjeti domain coverage.
 */
export async function fetchOpciDioStatus(payload: {
  building_type: string;
  building_subtype?: string | null;
  context?: string | null;
  work_type?: string;
  situacija?: SituacijaReport;
  recognized_docs?: RecognizedDocument[];
}): Promise<OpciDioStatus> {
  const data = await postJSON<unknown>("/api/opci-dio/status", payload);
  return opciDioStatusSchema.parse(data);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isBackendProjectId(projectId: string | undefined): projectId is string {
  return Boolean(projectId && UUID_RE.test(projectId));
}

export async function extractDxf(
  files: File[],
  projectId?: string,
  layerOverrides?: Record<string, string>,
): Promise<unknown> {
  const query = isBackendProjectId(projectId)
    ? `?${new URLSearchParams({ project_id: projectId }).toString()}`
    : "";
  const fields: Record<string, string> = layerOverrides
    ? { layer_overrides: JSON.stringify(layerOverrides) }
    : {};
  return await postFormData(`/api/extract-dxf${query}`, fields, files);
}

export async function saveLayerOverrides(
  projectId: string,
  overrides: Record<string, string>,
): Promise<unknown> {
  return await postJSON<unknown>(`/api/projects/${encodeURIComponent(projectId)}/layer-overrides`, {
    overrides,
  });
}

export async function extractIfc(file: File): Promise<unknown> {
  return await postFormData("/api/extract-ifc", {}, [file]);
}

// ---------------------------------------------------------------------------
// Natural-language prefill (voice / text / document) — Step 2 alternative path
// ---------------------------------------------------------------------------

export interface TranscribeResponse {
  status: "ok";
  text: string;
  language: string;
  duration_s: number | null;
}

/** Send an audio blob to Whisper. Returns the Croatian transcript +
 * detected language + duration. The caller is expected to show the
 * transcript to the user for review before calling ``extractFromText``. */
export async function transcribeAudio(audio: Blob, filename = "voice.webm"): Promise<TranscribeResponse> {
  const file = audio instanceof File ? audio : new File([audio], filename, { type: audio.type || "audio/webm" });
  return (await postSingleFile("/api/nl/transcribe", "audio", file)) as TranscribeResponse;
}

/** GPT-5.4 structured field extraction over a free-form Croatian
 * description. Returns the same ``{extracted, warnings}`` shape that
 * ``extractDxf`` / ``extractIfc`` return — so the existing prefill flow
 * works unchanged.
 *
 * ``alreadyFilled`` is the current ``useExtractionStore.confidence``
 * snapshot. The backend prompt reads it to skip DXF/IFC-owned fields
 * (so a voice memo can't clobber a measured value) and to refine
 * NL/form-owned ones. Pass ``undefined`` on first-pass extractions. */
export async function extractFromText(
  text: string,
  alreadyFilled?: Record<string, unknown>,
): Promise<unknown> {
  return await postJSON<unknown>("/api/nl/extract-from-text", {
    text,
    already_filled: alreadyFilled ?? null,
  });
}

/** PDF/DOCX → text → GPT-5.4 extraction. Same return shape as
 * ``extractFromText`` plus an extra ``source_text`` field so the UI can
 * show the architect what the model read.
 *
 * Multipart form fields: ``file`` (the document) plus ``already_filled``
 * (optional JSON string — backend parses leniently and drops it with a
 * warning if it can't). */
export async function extractFromDocument(
  file: File,
  alreadyFilled?: Record<string, unknown>,
): Promise<unknown> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  if (alreadyFilled && Object.keys(alreadyFilled).length > 0) {
    fd.append("already_filled", JSON.stringify(alreadyFilled));
  }
  const res = await apiFetch("/api/nl/extract-from-document", {
    method: "POST",
    body: fd,
  });
  if (!res.ok) {
    let message = "";
    try {
      const data = (await res.json()) as { detail?: string; message?: string };
      message = data?.message || data?.detail || "";
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, message || res.statusText);
  }
  return await res.json();
}

export async function fetchMunicipalities(): Promise<{
  municipalities: Array<{ code: string; name: string }>;
  default: string;
}> {
  const res = await apiFetch("/api/municipalities");
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return (await res.json()) as { municipalities: Array<{ code: string; name: string }>; default: string };
}

/** R1/H1 — resolve a k.o./JLS display name to a municipality slug.
 *  ``slug: null`` means "plan not in database": callers show the
 *  plan-missing banner instead of proceeding against another plan. */
export async function resolveJls(name: string): Promise<{
  name: string;
  slug: string | null;
  municipality_name: string | null;
  plan_version: string | null;
}> {
  const res = await apiFetch(`/api/jls/resolve?name=${encodeURIComponent(name)}`);
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return (await res.json()) as {
    name: string;
    slug: string | null;
    municipality_name: string | null;
    plan_version: string | null;
  };
}

export async function fetchCorpusStats(): Promise<CorpusStats> {
  const res = await apiFetch("/api/corpus-stats");
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return corpusStatsSchema.parse(await res.json());
}

export async function fetchBuildingTypes(): Promise<string[]> {
  const res = await apiFetch("/api/building-types");
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  const data = (await res.json()) as { building_types: string[] };
  return data.building_types;
}

/**
 * Trigger a browser download from a Blob. Used after every
 * ``exportDocuments`` call so the rest of the app doesn't have to
 * touch the DOM.
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Samostalni unos prostornog plana (self-serve spatial-plan intake)
//
// upload -> extract (background, poll status) -> rules review -> activate.
// The architect is the review gate: every staged limit carries its verbatim
// span + grounding status, and only approved, non-derived rules are promoted
// (confidence='user_approved_extraction').
// ---------------------------------------------------------------------------

export interface PlanCandidate {
  article_number: string;
  hints: string[];
}

// M1 (D4 koš) — role vocabulary of one document in the plan package.
export type PlanAttachmentKind =
  | "odredbe"
  | "odluka"
  | "obrazlozenje"
  | "izmjene"
  | "grafika"
  | "other";

export interface PlanAttachment {
  index: number;
  naziv: string;
  kind: PlanAttachmentKind;
  kind_label: string;
  consolidated: boolean;
  content_pattern: "full" | "amendment" | "mixed" | "unparseable" | null;
  articles_total: number | null;
  is_text_carrier: boolean;
  glasilo: string | null;
}

export interface PlanUploadResponse {
  plan_id: number;
  jls_name: string;
  municipality_code: string;
  plan_type: string;
  pdf_pages: number;
  articles_total: number;
  candidates: PlanCandidate[];
  fitness: "fit" | "degraded";
  warnings: string[];
  extract_cap: number;
  // H3 — content-pattern gate: an amendment PDF is saved but extraction
  // stays blocked until the pročišćeni tekst is supplied.
  content_pattern?: "full" | "amendment" | "mixed" | "unparseable";
  extraction_blocked?: boolean;
  // M1 koš — što sam prepoznao, po dokumentu.
  attachments?: PlanAttachment[];
  text_carrier_index?: number | null;
  is_consolidated?: boolean;
  // M2 — nadogradnja plana postojeće općine (null za običan upload):
  // stara pravila vrijede dok se nova ne potvrde i aktiviraju.
  superseding?: {
    municipality_code: string;
    municipality_name: string;
    previous_plan_version: string | null;
  } | null;
}

export interface PlanAttachmentPatchResponse {
  plan_id: number;
  attachments: PlanAttachment[];
  text_carrier_index: number | null;
  is_consolidated: boolean;
  content_pattern: string | null;
  extraction_blocked: boolean;
  restaged: boolean;
  // Recomputed only when the text carrier moved; null = keep current.
  candidates: PlanCandidate[] | null;
  extract_cap: number;
}

export interface PlanExtractionStatus {
  plan_id: number;
  state?: "idle" | "starting" | "extracting" | "done" | "error";
  total?: number;
  done?: number;
  rules_staged?: number;
  skipped_candidates?: number;
  // M2 — nastavak ekstrakcije: članci preskočeni jer već imaju pravila.
  already_covered?: number;
  errors?: string[];
  error?: string;
  staging_counts?: Record<string, number>;
}

export type KigTierKind = "pct" | "base_marginal" | "abs";

export interface KigTier {
  plot_min_m2: number;
  plot_max_m2: number | null;
  kind: KigTierKind;
  pct?: number | null;
  base_m2?: number | null;
  above_m2?: number | null;
  max_m2?: number | null;
  source_span?: string | null;
}

export interface StagedPlanRule {
  id: number;
  rule_subject_type: string | null;
  rule_subject_code: string | null;
  context_scope: string | null;
  geo_scope: string | null;
  building_type_canonical: string | null;
  kig_max: number | null;
  kig_tiers: KigTier[] | null;
  source_text: string | null;
  kisn_max: number | null;
  max_floors: number | null;
  max_height_v_m: number | null;
  max_total_height_v_m: number | null;
  min_plot_size_m2: number | null;
  min_distance_reg_line_m: number | null;
  min_distance_boundary_m: number | null;
  min_green_area_pct: number | null;
  max_building_length_m: number | null;
  applies_when: string | null;
  source_article: string | null;
  grounding_status: string;
  value_spans: Record<string, string> | null;
  derived: boolean;
  review_status: string;
}

export async function uploadSpatialPlan(
  files: File[],
  jlsName: string,
  planType: string,
  supersede = false,
): Promise<PlanUploadResponse> {
  const fd = new FormData();
  for (const f of files) fd.append("files", f, f.name);
  fd.append("jls_name", jlsName);
  fd.append("plan_type", planType);
  if (supersede) fd.append("supersede", "true");
  const res = await apiFetch("/api/spatial-plans/upload", { method: "POST", body: fd });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: string };
      detail = data?.detail ?? "";
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  return (await res.json()) as PlanUploadResponse;
}

/** M1 koš — correct a document's role / consolidated flag, or record its
 *  glasilo (SN/NN). Role changes may move the text carrier (restaged). */
export async function updatePlanAttachment(
  planId: number,
  index: number,
  patch: { kind?: PlanAttachmentKind; consolidated?: boolean; glasilo?: string | null },
): Promise<PlanAttachmentPatchResponse> {
  const res = await apiFetch(`/api/spatial-plans/${planId}/attachments/${index}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: string };
      detail = data?.detail ?? "";
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  return (await res.json()) as PlanAttachmentPatchResponse;
}

export async function startPlanExtraction(planId: number): Promise<unknown> {
  return await postJSON<unknown>(`/api/spatial-plans/${planId}/extract`, {});
}

export async function fetchPlanExtractionStatus(
  planId: number,
): Promise<PlanExtractionStatus> {
  const res = await apiFetch(`/api/spatial-plans/${planId}/status`);
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return (await res.json()) as PlanExtractionStatus;
}

export async function fetchPlanRules(
  planId: number,
): Promise<{ plan_id: number; rules: StagedPlanRule[] }> {
  const res = await apiFetch(`/api/spatial-plans/${planId}/rules`);
  if (!res.ok) throw new ApiError(res.status, res.statusText);
  return (await res.json()) as { plan_id: number; rules: StagedPlanRule[] };
}

export async function reviewPlanRules(
  planId: number,
  approveIds: number[],
  rejectIds: number[],
): Promise<unknown> {
  return await postJSON<unknown>(`/api/spatial-plans/${planId}/review`, {
    approve_ids: approveIds,
    reject_ids: rejectIds,
  });
}

export async function saveRuleTiers(
  planId: number,
  ruleId: number,
  payload: { kig_tiers: KigTier[] | null; geo_scope: string | null },
): Promise<{ plan_id: number; rule_id: number; kig_tiers: KigTier[] | null; geo_scope: string | null; kig_max: number | null }> {
  const res = await apiFetch(`/api/spatial-plans/${planId}/rules/${ruleId}/tiers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: string };
      detail = data?.detail ?? "";
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  return await res.json();
}

/** Canonical building-type families the engine knows (+ "not a building"). */
export const CANONICAL_FAMILIES: Array<{ value: string; label: string }> = [
  { value: "individualna_stambena", label: "obiteljska / individualna stambena" },
  { value: "visestambena", label: "višestambena (zgrada)" },
  { value: "ugostiteljsko_turisticka", label: "ugostiteljsko-turistička" },
  { value: "poslovna", label: "poslovna" },
  { value: "proizvodna", label: "proizvodna" },
  { value: "drustvena", label: "javna i društvena" },
  { value: "poljoprivredna", label: "poljoprivredna / gospodarska" },
  { value: "pomocna", label: "pomoćna" },
  { value: "jednostavna", label: "jednostavna" },
  { value: "nije_zgrada", label: "nije zgrada (infrastruktura)" },
];

export async function setRuleCanonical(
  planId: number,
  ruleId: number,
  canonical: string | null,
): Promise<{ plan_id: number; rule_id: number; building_type_canonical: string | null }> {
  return await postJSON(`/api/spatial-plans/${planId}/rules/${ruleId}/canonical`, { canonical });
}

export async function activateSpatialPlan(planId: number): Promise<{
  plan_id: number;
  municipality: { code: string; name: string };
  promoted: number;
  skipped_derived: number;
}> {
  return await postJSON(`/api/spatial-plans/${planId}/activate`, {});
}
